/**
 * RetentionQuizToggleCard.jsx — settings card with a single toggle that
 * controls whether the user gets the optional 3-question retention
 * quiz after every Book Series episode finish (+2 coins per correct).
 *
 * Feb 12 2026 user spec: *"Ask users if they want to earn coins by
 * answering 3 questions to test retention. Can be toggled on and off."*
 */
import React, { useEffect, useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RetentionQuizToggleCard = () => {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/profile/retention-quiz`);
        const d = await r.json();
        setEnabled(!!d.enabled);
      } catch {
        setEnabled(true); // default on
      }
    })();
  }, []);

  const flip = async () => {
    if (enabled === null) return;
    const next = !enabled;
    setSaving(true);
    setEnabled(next); // optimistic
    try {
      const r = await authFetch(`${BACKEND_URL}/api/profile/retention-quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next, prompted: true }),
      });
      if (!r.ok) throw new Error();
      toast.success(next ? 'Retention quiz turned on' : 'Retention quiz turned off');
    } catch {
      setEnabled(!next);
      toast.error('Could not save preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="retention-quiz-toggle-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-300" /> Retention quiz bonus
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-300/90 leading-relaxed">
          After every Book Series episode, answer <strong className="text-white">3 quick questions</strong> and
          earn an extra <strong className="text-amber-200">+2 coins per correct answer</strong> (up to +6 per episode).
        </p>
        <button
          type="button"
          onClick={flip}
          disabled={enabled === null || saving}
          className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 hover:border-amber-400/40 transition"
          data-testid="retention-quiz-toggle-btn"
        >
          <span className="text-sm font-bold text-white">
            {enabled === null ? 'Loading…' : enabled ? 'On — quiz me after each episode' : 'Off — skip the quiz'}
          </span>
          <span
            className={`relative inline-flex items-center w-12 h-6 rounded-full transition-colors ${
              enabled ? 'bg-amber-400' : 'bg-slate-700'
            }`}
            aria-checked={!!enabled}
            role="switch"
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin absolute left-1.5 top-1.5 text-white" />}
            <span
              className={`absolute top-0.5 ${enabled ? 'left-6' : 'left-0.5'} w-5 h-5 rounded-full bg-white shadow transition-all`}
            />
          </span>
        </button>
        <p className="text-[11px] text-slate-500">
          Each question is generated from the episode you just read. One attempt per episode.
        </p>
      </CardContent>
    </Card>
  );
};

export default RetentionQuizToggleCard;
